import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import PaystackPop from '@paystack/inline-js';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import GlobalHeader from '../../../../components/GlobalHeader.jsx';
import EventImage from '../../../../components/EventImage.jsx';
import axiosInstance from '../../../../utils/api/axiosInstance.js';
import { ticketService } from '../../../../utils/ticketService.js';
import { useLiveAvailability } from '../../../../hooks/useLiveAvailability.js';
import { getDisplayPrice, calculateServiceFee, formatPrice, parseAmountToNumber } from '../../../../utils/priceUtils.js';
import ConfirmPackagesCard from '../components/ConfirmPackagesCard.jsx';
import '../../TicketDetails/css/ConfirmTicketPackage.css';

const getUserIdFromToken = (token) => {
  if (!token) return null;
  try {
    const payload = JSON.parse(atob(token.split('.')[1]));
    return payload.userId || payload.user_id;
  } catch (error) {
    console.error('Error extracting userId from token:', error);
    return null;
  }
};

const PAYSTACK_CURRENCY = 'ZAR';

const defaultDelegations = ['Delegate', 'Speaker', 'Exhibitor', 'Visitor'];

const ConfirmTicketPackage = () => {
  const nav = useNavigate();
  const { eventId, packageIndex } = useParams();
  const location = useLocation();

  const [showModal, setShowModal] = useState(false);
  const [event, setEvent] = useState(null);
  const [delegations, setDelegations] = useState(defaultDelegations);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [submitting, setSubmitting] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [generalError, setGeneralError] = useState('');
  
  // State for the actual PackageID
  const [actualPackageId, setActualPackageId] = useState(null);

  // Add live availability hook - only use when we have the actual PackageID
  const { availability, loading: availabilityLoading, error: availabilityError, isConnected } = useLiveAvailability(
    actualPackageId ? actualPackageId.toString() : null
  );

  // Derive PackageID from the same event.packages array that packageIndex refers to.
  // Do NOT fetch /packages/event separately — that endpoint orders by PackageID which
  // may differ from the json_agg order in the shared events endpoint.
  useEffect(() => {
    if (event?.packages && Array.isArray(event.packages) && packageIndex !== undefined) {
      const pkg = event.packages[packageIndex];
      const id = pkg?.package_id || pkg?.PackageID || null;
      if (id) {
        setActualPackageId(id);
      }
    }
  }, [event, packageIndex]);
  
  const [packages, setPackages] = useState(() => {
    let initialPackages = [];

    if (location.state?.delegates) {
      initialPackages = location.state.delegates;
    } else {
      const storedPackages = localStorage.getItem('currentPackages');
      if (storedPackages) {
        localStorage.removeItem('currentPackages');
        try {
          initialPackages = JSON.parse(storedPackages);
        } catch (e) {
          console.error('Error parsing stored packages:', e);
        }
      }
    }

    if (initialPackages.length > 0) {
      initialPackages = initialPackages.map((pkg, index) => {
        console.log(`[ConfirmTicketPackage init] Package ${index} incoming:`, { amount: pkg.amount, unitPrice: pkg.unitPrice });
        // Use unitPrice as the raw DB price if already set; otherwise derive from amount
        let dbPrice;
        if (pkg.unitPrice) {
          dbPrice = pkg.unitPrice;
        } else if (pkg.amount) {
          dbPrice = parseAmountToNumber(pkg.amount);
          pkg.unitPrice = dbPrice;
          console.log(`[ConfirmTicketPackage init] Set unitPrice=${dbPrice} from amount=${pkg.amount}`);
        }
        if (dbPrice) {
          const displayPrice = getDisplayPrice(dbPrice);
          const formattedAmount = formatPrice(displayPrice);
          console.log(`[ConfirmTicketPackage init] Package ${index}: dbPrice=${dbPrice}, displayPrice=${displayPrice}, formattedAmount=${formattedAmount}`);
          pkg.amount = formattedAmount;
        }
        return pkg;
      });
    }

    return initialPackages;
  });

  // Update packages with actualPackageId when it becomes available
  useEffect(() => {
    if (actualPackageId && packages.length > 0) {
      setPackages(prevPackages => 
        prevPackages.map((pkg, index) => ({
          ...pkg,
          packageId: actualPackageId
        }))
      );
    }
  }, [actualPackageId, packages.length]);

  const paystackPublicKey =
    (typeof window !== "undefined" && window.__ENV__?.PAYSTACK_PUBLIC_KEY) ||
    process.env.REACT_APP_PAYSTACK_PUBLIC_KEY;

  const removePackage = (index) => {
    const updatedPackages = [...packages];
    updatedPackages.splice(index, 1);
    setPackages(updatedPackages);
  };

  const updatePackageDetail = (index, field, value) => {
    const updatedPackages = [...packages];
    const newValidationErrors = { ...validationErrors };
    const errorKey = `${index}-${field}`;

    delete newValidationErrors[errorKey];

    if (field === 'name') {
      const nameRegex = /^[A-Za-z\s-]*$/;
      if (!nameRegex.test(value)) {
        newValidationErrors[errorKey] = 'Name can only contain letters, spaces, and hyphens';
        setValidationErrors(newValidationErrors);
        return;
      }
      if (!value || value.trim() === '') {
        newValidationErrors[errorKey] = 'Name is required';
      }
    } else if (field === 'email') {
      if (!value || value.trim() === '') {
        newValidationErrors[errorKey] = 'Email is required';
      } else {
        const emailRegex =
          /^[^\s@]+@(gmail\.com|outlook\.com|yahoo\.com|hotmail\.com|icloud\.com|.*\.edu|.*\.org|.*\.co\.za)$/i;
        if (!emailRegex.test(value)) {
          newValidationErrors[errorKey] = 'Please enter a valid email address with a common domain';
        }
      }
    } else if (field === 'phone') {
      if (!value || value.trim() === '') {
        newValidationErrors[errorKey] = 'Phone number is required';
      } else {
        const phoneRegex = /^[0-9]*$/;
        if (!phoneRegex.test(value)) {
          newValidationErrors[errorKey] = 'Phone number can only contain digits';
          setValidationErrors(newValidationErrors);
          return;
        }
        if (value.length > 0 && value[0] !== '0') {
          value = '0' + value.substring(value.length > 1 ? 1 : 0);
        }
        if (value.length > 10) {
          value = value.substring(0, 10);
        }
        if (value.length > 0 && value.length < 10) {
          newValidationErrors[errorKey] = 'Phone number must be exactly 10 digits';
        }
      }
    } else if (field === 'ieeeNumber') {
      if (value && value.trim() !== '') {
        const ieeeRegex = /^[0-9]*$/;
        if (!ieeeRegex.test(value)) {
          newValidationErrors[errorKey] = 'IEEE number can only contain digits';
          setValidationErrors(newValidationErrors);
          return;
        }
        if (value.length > 8) {
          value = value.substring(0, 8);
        }
        if (value.length > 0 && value.length < 8) {
          newValidationErrors[errorKey] = 'IEEE number must be exactly 8 digits';
        }
      }
    }

    setValidationErrors(newValidationErrors);

    updatedPackages[index][field] = value;

    if (field === 'tickets') {
      const basePrice =
        typeof updatedPackages[index].unitPrice === 'string'
          ? Number.parseFloat(updatedPackages[index].unitPrice.replace(/,/g, ''))
          : updatedPackages[index].unitPrice;

      const ticketCount = Number.parseInt(value, 10) || 1;
      const safeTicketCount = ticketCount < 1 ? 1 : ticketCount;
      const displayUnitPrice = getDisplayPrice(basePrice);
      const totalAmount = displayUnitPrice * safeTicketCount;

      updatedPackages[index].amount = formatPrice(totalAmount);

      if (safeTicketCount !== ticketCount) {
        updatedPackages[index].tickets = safeTicketCount.toString();
      }
    }

    setPackages(updatedPackages);
  };

  useEffect(() => {
    const fetchEventDetails = async () => {
      try {
        setLoading(true);

        const token = localStorage.getItem('token');
        if (!token) {
          nav('/');
          return;
        }

        console.log('[ConfirmTicketPackage] 📋 Fetching event — API: GET /shared/events/:eventId', { eventId, packageIndex });
        const response = await axiosInstance.get(`/shared/events/${eventId}`);
        const data = response.data;
        console.log('[ConfirmTicketPackage] ✅ Event loaded:', {
          columns: 'event_id, name, location, start_date, packages[{PackageID,name,price,max_tickets,sold_tickets,held_tickets,valid_from_date,valid_to_date}], attendees',
          packageCount: data?.packages?.length,
        });
        setEvent(data);

        if (data.attendees) {
          try {
            let parsedAttendees;
            if (typeof data.attendees === 'string') {
              parsedAttendees = JSON.parse(data.attendees);
            } else {
              parsedAttendees = data.attendees;
            }
            const attendees = Array.isArray(parsedAttendees)
              ? parsedAttendees.filter((type) => type && type.trim() !== '')
              : [];
            if (attendees.length > 0) {
              setDelegations(attendees);
            } else {
              setDelegations(defaultDelegations);
            }
          } catch (e) {
            console.error('Error parsing attendees:', e);
            setDelegations(defaultDelegations);
          }
        } else {
          setDelegations(defaultDelegations);
        }
      } catch (err) {
        console.error('Error fetching event details:', err);
        setError(err.message || 'Failed to load event');
      } finally {
        setLoading(false);
      }
    };

    if (eventId && packageIndex) {
      fetchEventDetails();
    }
  }, [eventId, packageIndex, nav]);

  const handleAddPackage = () => setShowModal(true);
  const handleCloseModal = () => setShowModal(false);

  const validatePackage = (pkg, index) => {
    const errors = {};
    const errorKey = (field) => `${index}-${field}`;

    if (!pkg.name || pkg.name.trim() === '') {
      errors[errorKey('name')] = 'Name is required';
    } else if (!/^[A-Za-z\s-]*$/.test(pkg.name)) {
      errors[errorKey('name')] = 'Name can only contain letters, spaces, and hyphens';
    }

    if (!pkg.email || pkg.email.trim() === '') {
      errors[errorKey('email')] = 'Email is required';
    } else {
      const emailRegex =
        /^[^\s@]+@(gmail\.com|outlook\.com|yahoo\.com|hotmail\.com|icloud\.com|.*\.edu|.*\.org|.*\.co\.za)$/i;
      if (!emailRegex.test(pkg.email)) {
        errors[errorKey('email')] = 'Please enter a valid email address with a common domain';
      }
    }

    if (!pkg.phone || pkg.phone.trim() === '') {
      errors[errorKey('phone')] = 'Phone number is required';
    } else if (!/^[0-9]*$/.test(pkg.phone)) {
      errors[errorKey('phone')] = 'Phone number can only contain digits';
    } else if (pkg.phone.length !== 10) {
      errors[errorKey('phone')] = 'Phone number must be exactly 10 digits';
    } else if (pkg.phone[0] !== '0') {
      errors[errorKey('phone')] = 'Phone number must start with 0';
    }

    if (pkg.ieeeNumber && pkg.ieeeNumber.trim() !== '') {
      if (!/^[0-9]*$/.test(pkg.ieeeNumber)) {
        errors[errorKey('ieeeNumber')] = 'IEEE number can only contain digits';
      } else if (pkg.ieeeNumber.length !== 8) {
        errors[errorKey('ieeeNumber')] = 'IEEE number must be exactly 8 digits';
      }
    }

    return errors;
  };

  const handleSubmitPurchase = async () => {
    if (packages.length === 0) {
      toast.error('Please add at least one package to purchase');
      return;
    }

    // Check availability before proceeding
    if (!availability || availability.available === undefined) {
      toast.error('Ticket availability information is not available. Please refresh the page and try again.');
      return;
    }

    // Calculate total tickets needed
    const totalTickets = packages.reduce((sum, pkg) => sum + (Number.parseInt(pkg.tickets, 10) || 1), 0);
    
    // Check if enough tickets are available
    if (totalTickets > availability.available) {
      toast.error(`Only ${availability.available} tickets are available. You're trying to purchase ${totalTickets} tickets.`);
      return;
    }

    let allErrors = {};
    let hasErrors = false;

    packages.forEach((pkg, index) => {
      const packageErrors = validatePackage(pkg, index);
      if (Object.keys(packageErrors).length > 0) {
        allErrors = { ...allErrors, ...packageErrors };
        hasErrors = true;
      }
    });

    setValidationErrors(allErrors);

    if (hasErrors) {
      toast.error('Please fix all validation errors before submitting.');
      return;
    }

    try {
      setSubmitting(true);

      const token = localStorage.getItem('token');
      const userId = getUserIdFromToken(token);

      if (!token || !userId) {
        toast.error('Your session has expired. Please log in again to complete your purchase.');
        nav('/');
        setSubmitting(false);
        return;
      }

      if (!paystackPublicKey) {
        toast.error('Paystack public key is not configured. Please contact support.');
        setSubmitting(false);
        return;
      }

      // subtotal = sum of flat DB prices × ticket counts
      // serviceFee = 3% of subtotal + R1 (covers Paystack's 2.9% + R1 with a small buffer)
      // totalAmount = what the customer is charged and what gets sent to Paystack
      const subtotal = packages.reduce((sum, pkg) => sum + parseAmountToNumber(pkg.amount), 0);
      const serviceFee = calculateServiceFee(subtotal);
      const totalAmount = subtotal > 0 ? Math.round((subtotal + serviceFee) * 100) / 100 : 0;
      console.log('[ConfirmTicketPackage] 💰 Pricing breakdown:', {
        subtotal,
        serviceFeeFormula: `3% × subtotal + R1 = ${serviceFee}`,
        totalAmount,
        packageAmounts: packages.map((pkg) => ({ name: pkg.packageDetails || pkg.name, amount: pkg.amount })),
      });

      if (totalAmount <= 0) {
        toast.error('Unable to determine total amount for Paystack checkout. Please review your packages.');
        setSubmitting(false);
        return;
      }

      // Step 1: Reserve tickets using our new system
      console.log('Event data:', event);
      console.log('Package index:', packageIndex);
      console.log('Packages array:', event?.packages);
      console.log('Selected package:', event?.packages?.[packageIndex]);
      
      let reservation;
      
      // Use the actual PackageID from state
      const packageIdToUse = actualPackageId;
      console.log('Using PackageID:', packageIdToUse);
      
      if (!packageIdToUse && packageIdToUse !== 0) {
        toast.error('Package information not available. Please refresh the page and try again.');
        setSubmitting(false);
        return;
      }
      
      console.log(`Reserving ${totalTickets} tickets for package ${packageIdToUse}`);
      reservation = await ticketService.reserveTickets(packageIdToUse, totalTickets);
      
      console.log('Reservation successful:', reservation);
      
      const primaryEmail =
        packages.find((pkg) => pkg.email)?.email ||
        event?.contact_email ||
        'payments@xpressticket.com';

      // Step 2: Initialize split payment server-side so Paystack embeds the
      // subaccount split into the transaction session (access_code).
      // If the organiser has no subaccount yet, fall back to direct payment.
      let accessCode = null;
      let paymentReference = reservation.reservationId;
      try {
        console.log('[ConfirmTicketPackage] 🔄 Initializing split payment — API: POST /payments/initialize', { email: primaryEmail, amount: totalAmount, subtotal, reference: reservation.reservationId });
        const splitResponse = await axiosInstance.post('/payments/initialize', {
          email: primaryEmail,
          amount: totalAmount,
          originalAmount: subtotal,
          reference: reservation.reservationId,
          eventId: eventId,
          packageId: packageIdToUse
        });
        accessCode = splitResponse.data.access_code || null;
        paymentReference = splitResponse.data.reference || reservation.reservationId;
        console.log('✅ Split payment initialized, access_code:', accessCode ? 'received' : 'missing');
      } catch (error) {
        console.warn('⚠️ Split payment init failed, falling back to direct payment:', error.response?.data?.message || error.message);
        // Direct payment — full amount goes to main account
      }

      // Step 3: Open Paystack.
      // `PaystackPop.setup()` + `handler.openIframe()` is the DEPRECATED v1 API and is
      // not reliable for resuming a server-initialized transaction via access_code in
      // this library version (v2) — it can throw instead of opening the checkout.
      // Use the documented v2 API instead: `new PaystackPop().resumeTransaction(accessCode, callbacks)`
      // when access_code is present (split/direct-subaccount session already initialized
      // server-side), or `.newTransaction({...})` as a fallback when it isn't.
      const handleTransactionSuccess = async (response) => {
        try {
          console.log('[ConfirmTicketPackage] ✅ Paystack success — reference:', response.reference);
          console.log('[ConfirmTicketPackage] 🔄 Verifying + creating tickets — API: POST /paystack/verify', {
            amountScalingFormula: 'pkg.amount × (totalAmount / subtotal) so sum(amounts) = totalAmount = amountPaid',
            subtotal,
            totalAmount,
          });

          // Scale each package amount so requestedTotal = totalAmount = amountPaid.
          // verifyPayment rejects if |requestedTotal - amountPaid| > 0.5.
          const verificationResponse = await axiosInstance.post('/paystack/verify', {
            reference: response.reference,
            event_id: eventId,
            user_id: userId,
            packages: packages.map((pkg) => ({
              packageId: pkg.packageId,
              tickets: Number.parseInt(pkg.tickets, 10) || 1,
              amount: subtotal > 0
                ? Math.round(parseAmountToNumber(pkg.amount) * (totalAmount / subtotal) * 100) / 100
                : 0,
              packageDetails: pkg.packageDetails || pkg.name || 'Standard Package',
              delegate_details: {
                title: pkg.title,
                name: pkg.name,
                gender: pkg.gender,
                email: pkg.email,
                phone: pkg.phone,
                delegation: pkg.delegation,
                ieeeNumber: pkg.ieeeNumber,
                dayPass: pkg.dayPass,
              },
            })),
          });

          console.log('✅ Ticket creation successful:', verificationResponse.data);
          toast.success('Payment successful! Your tickets have been created.');

          nav('/receipt', {
            state: {
              event,
              packages: packages.map((pkg) => ({
                ...pkg,
                amount: formatPrice(parseAmountToNumber(pkg.amount)),
                status: 'Approved',
              })),
              totalAmount,
              paymentReference: response.reference,
              reservationId: reservation.reservationId,
            },
          });
        } catch (error) {
          console.error('❌ Ticket creation failed:', error);
          const serverMessage = error.response?.data?.message;
          toast.error(
            serverMessage
              ? `Payment received but ticket creation failed: ${serverMessage}. Please contact support with reference: ${response.reference}`
              : `Payment received but ticket creation failed. Please contact support with reference: ${response.reference}`
          );
        } finally {
          setSubmitting(false);
        }
      };

      const handleTransactionCancel = () => {
        console.log('Payment modal closed');
        setSubmitting(false);
      };

      const handleTransactionError = ({ message } = {}) => {
        console.error('Paystack transaction error:', message);
        toast.error(message || 'Payment could not be started. Please try again.');
        setSubmitting(false);
      };

      const paystackInstance = new PaystackPop();

      if (accessCode) {
        paystackInstance.resumeTransaction(accessCode, {
          onSuccess: handleTransactionSuccess,
          onCancel: handleTransactionCancel,
          onError: handleTransactionError,
        });
      } else {
        paystackInstance.newTransaction({
          key: paystackPublicKey,
          email: primaryEmail,
          amount: Math.round(totalAmount * 100),
          currency: PAYSTACK_CURRENCY,
          reference: paymentReference,
          metadata: {
            eventId,
            userId,
            packageCount: packages.length,
            // Scale each package's amount so the sum equals totalAmount (subtotal + serviceFee).
            // verifyPayment cross-checks sum(pkg.amount) against amountPaid — they must match.
            packages: packages.map((pkg) => ({
              packageId: pkg.packageId,
              packageDetails: pkg.packageDetails || pkg.name || 'Standard Package',
              tickets: Number.parseInt(pkg.tickets, 10) || 1,
              amount: subtotal > 0
                ? Math.round(parseAmountToNumber(pkg.amount) * (totalAmount / subtotal) * 100) / 100
                : 0,
              delegate_details: {
                title: pkg.title,
                name: pkg.name,
                gender: pkg.gender,
                email: pkg.email,
                phone: pkg.phone,
                delegation: pkg.delegation,
                ieeeNumber: pkg.ieeeNumber,
                dayPass: pkg.dayPass,
              },
            })),
            reservationId: reservation.reservationId,
          },
          onSuccess: handleTransactionSuccess,
          onCancel: handleTransactionCancel,
          onError: handleTransactionError,
        });
      }
    } catch (err) {
      console.error('Error in reservation process:', err);
      setSubmitting(false);
      
      if (err.message.includes('Sold out') || err.message.includes('insufficient')) {
        toast.error('Sorry, there are not enough tickets available for this package. Please try a different package or check back later.');
      } else if (err.message.includes('Authentication')) {
        toast.error('Your session has expired. Please log in again.');
        nav('/');
      } else {
        toast.error(err.message || 'Failed to reserve tickets. Please try again.');
      }
    }
  };

  const handleSelectPackage = async (pkg) => {
    try {
      const pathParts = pkg.path.split('/');
      const selectedPackageIndex = pathParts[pathParts.length - 1];
      const selectedPackage = event.packages[selectedPackageIndex];

      let parsedPackage;
      if (typeof selectedPackage === 'string') {
        try {
          if (selectedPackage.startsWith('{') && selectedPackage.endsWith('}')) {
            const cleanedStr = selectedPackage.replace(/\\\\|\\"/g, '"');
            parsedPackage = JSON.parse(cleanedStr);
          } else {
            const pricingMatch = selectedPackage.match(/"pricing":"\\"([^\\]+)\\""/i);
            const typeMatch = selectedPackage.match(/"selectType":"\\"([^\\]+)\\""/i);

            parsedPackage = {
              name: typeMatch ? typeMatch[1] : pkg.name,
              price: pricingMatch ? pricingMatch[1] : pkg.price.replace('R ', ''),
            };
          }
        } catch (e) {
          console.error('Error parsing package data:', e);
          parsedPackage = {
            name: pkg.name,
            price: pkg.price.replace('R ', ''),
          };
        }
      } else {
        parsedPackage = selectedPackage;
      }

      const dbPrice = pkg.dbPrice || parseAmountToNumber(pkg.price);
      const displayPrice = getDisplayPrice(dbPrice);

      const newPackage = {
        packageId: selectedPackage.PackageID || selectedPackage.packageId || pkg.id,
        packageDetails: parsedPackage.name || parsedPackage.selectType || pkg.name,
        title: 'Prof',
        name: '',
        gender: 'Male',
        email: '',
        phone: '',
        delegation: 'student1',
        tickets: '1',
        ieeeNumber: '',
        dayPass: parsedPackage.Duration || '',
        amount: formatPrice(displayPrice),
        unitPrice: dbPrice,
        isNewlyAdded: true,
      };

      setPackages([...packages, newPackage]);
      handleCloseModal();
    } catch (error) {
      console.error('Error adding new package:', error);
      toast.error('Failed to add the selected package. Please try again.');
    }
  };

  const [packageOptions, setPackageOptions] = useState([]);

  useEffect(() => {
    if (event && event.packages && Array.isArray(event.packages)) {
      const options = event.packages.map((pkg, idx) => {
        let parsedPackage;
        if (typeof pkg === 'string') {
          try {
            if (pkg.startsWith('{') && pkg.endsWith('}')) {
              const cleanedStr = pkg.replace(/\\\\|\\"/g, '"');
              parsedPackage = JSON.parse(cleanedStr);
            } else {
              const pricingMatch = pkg.match(/"pricing":"\\"([^\\]+)\\""/i);
              const typeMatch = pkg.match(/"selectType":"\\"([^\\]+)\\""/i);

              parsedPackage = {
                name: typeMatch ? typeMatch[1] : `Package ${idx + 1}`,
                price: pricingMatch ? pricingMatch[1] : '0.00',
              };
            }
          } catch (e) {
            console.error('Error parsing package data:', e);
            parsedPackage = { name: `Package ${idx + 1}`, price: '0.00' };
          }
        } else {
          parsedPackage = pkg;
        }

        const rawPrice = parsedPackage.price || parsedPackage.pricing || '0.00';
        const option = {
          name: parsedPackage.name || parsedPackage.selectType || `Package ${idx + 1}`,
          price: formatPrice(getDisplayPrice(rawPrice)),
          dbPrice: parseAmountToNumber(rawPrice),
          path: `/customerticketdetails1/${eventId}/${idx}`,
          sold: parsedPackage.sold_tickets || 0, // Use sold_tickets from backend
          held: parsedPackage.held_tickets || 0, // Use held_tickets from backend
          total: parsedPackage.max_tickets || parsedPackage.capacity || 0, // Use max_tickets from backend
          packageId: parsedPackage.package_id || idx, // Use package_id from backend
          // Add date validity fields
          validFrom: parsedPackage.valid_from_date,
          validTo: parsedPackage.valid_to_date,
          validFromDate: parsedPackage.valid_from_date,
          validToDate: parsedPackage.valid_to_date,
        };

        console.log(`🔍 Package ${idx} created:`, {
          name: option.name,
          sold: option.sold,
          held: option.held,
          total: option.total,
          soldPlusHeld: option.sold + option.held,
          packageId: option.packageId,
          validFrom: option.validFrom,
          validTo: option.validTo,
          originalPackage: parsedPackage
        });

        return option;
      });

      setPackageOptions(options);
    }
  }, [event, eventId]);

  // Create live availability component for header
  const LiveAvailabilityHeader = () => {
    if (availabilityLoading) {
      return (
        <div style={{ 
          padding: '10px 20px',
          backgroundColor: '#f8f9fa',
          borderRadius: '8px',
          border: '1px solid #e9ecef',
          textAlign: 'center',
          minWidth: '300px'
        }}>
          <div style={{ fontSize: '14px', color: '#666' }}>Loading availability...</div>
        </div>
      );
    }

    if (availabilityError) {
      return (
        <div style={{ 
          padding: '10px 20px',
          backgroundColor: '#f8d7da',
          borderRadius: '8px',
          border: '1px solid #f5c6cb',
          textAlign: 'center',
          minWidth: '300px'
        }}>
          <div style={{ fontSize: '14px', color: '#721c24' }}>Unable to load availability</div>
        </div>
      );
    }

    if (!availability) {
      return null;
    }

    return (
      <div style={{ 
        padding: '10px 20px',
        backgroundColor: '#f8f9fa',
        borderRadius: '8px',
        border: '1px solid #e9ecef',
        minWidth: '400px'
      }}>
        <div style={{ 
          display: 'flex', 
          justifyContent: 'space-between', 
          alignItems: 'center',
          marginBottom: '8px'
        }}>
          <h4 style={{ margin: 0, color: '#333', fontSize: '16px' }}>Live Availability</h4>
          <div style={{ 
            display: 'flex', 
            alignItems: 'center',
            fontSize: '10px',
            color: '#666'
          }}>
            <div style={{ 
              width: '6px', 
              height: '6px', 
              borderRadius: '50%', 
              backgroundColor: isConnected ? '#28a745' : '#dc3545',
              marginRight: '3px'
            }}></div>
            {isConnected ? 'Live' : 'Offline'}
          </div>
        </div>
        
        <div style={{ 
          display: 'grid', 
          gridTemplateColumns: 'repeat(4, 1fr)', 
          gap: '10px',
          textAlign: 'center'
        }}>
          <div style={{ 
            padding: '5px', 
            backgroundColor: '#d4edda', 
            borderRadius: '4px',
            border: '1px solid #c3e6cb'
          }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#155724' }}>
              {availability.available}
            </div>
            <div style={{ fontSize: '9px', color: '#155724' }}>Available</div>
          </div>
          
          <div style={{ 
            padding: '5px', 
            backgroundColor: '#d1ecf1', 
            borderRadius: '4px',
            border: '1px solid #bee5eb'
          }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#0c5460' }}>
              {availability.total}
            </div>
            <div style={{ fontSize: '9px', color: '#0c5460' }}>Total</div>
          </div>
          
          <div style={{ 
            padding: '5px', 
            backgroundColor: '#fff3cd', 
            borderRadius: '4px',
            border: '1px solid #ffeeba'
          }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#856404' }}>
              {availability.held}
            </div>
            <div style={{ fontSize: '9px', color: '#856404' }}>Reserved</div>
          </div>
          
          <div style={{ 
            padding: '5px', 
            backgroundColor: '#f8d7da', 
            borderRadius: '4px',
            border: '1px solid #f5c6cb'
          }}>
            <div style={{ fontSize: '16px', fontWeight: 'bold', color: '#721c24' }}>
              {availability.sold}
            </div>
            <div style={{ fontSize: '9px', color: '#721c24' }}>Sold</div>
          </div>
        </div>
        
        {availability.updatedAt && (
          <div style={{ 
            fontSize: '9px', 
            color: '#666', 
            textAlign: 'right',
            marginTop: '5px'
          }}>
            Updated: {new Date(availability.updatedAt).toLocaleTimeString()}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="ticket-details-page ticket-details-page--confirm">
      <GlobalHeader
        title={event?.name || "Event"}
        subtitle=""
        backTo={`/customerticketdetails1/${eventId}/${packageIndex}`}
        logoutTo="/"
      />

      <main className="ticket-details-page__main">
        {loading ? (
          <div className="ticket-details-loading">
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>
               d a            </div>
            <div>Loading event details...</div>
          </div>
        ) : error ? (
          <div className="ticket-details-error">
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>
               d a            </div>
            <div>Error: {error}</div>
          </div>
        ) : event ? (
          <div className="ticket-confirm-main-card ticket-card">
            {(() => {
              const displaySubtotal = packages.reduce((sum, pkg) => sum + parseAmountToNumber(pkg.amount), 0);
              const displayServiceFee = calculateServiceFee(displaySubtotal);
              const displayTotal = displaySubtotal > 0 ? Math.round((displaySubtotal + displayServiceFee) * 100) / 100 : 0;
              return (
                <ConfirmPackagesCard
                  event={event}
                  packages={packages}
                  delegations={delegations}
                  validationErrors={validationErrors}
                  onUpdatePackageDetail={updatePackageDetail}
                  onRemovePackage={removePackage}
                  onAddPackage={handleAddPackage}
                  onSubmitPurchase={handleSubmitPurchase}
                  submitting={submitting}
                  packageOptions={packageOptions}
                  showModal={showModal}
                  onSelectPackage={handleSelectPackage}
                  onCloseModal={handleCloseModal}
                  subtotal={displaySubtotal}
                  serviceFee={displayServiceFee}
                  totalAmount={displayTotal}
                />
              );
            })()}
          </div>
        ) : (
          <div className="ticket-details-error">
            <div style={{ fontSize: '24px', marginBottom: '10px' }}>
                   </div>
            <div>No event data available</div>
          </div>
        )}
      </main>
      
      <ToastContainer
        position="top-right"
        autoClose={5000}
        hideProgressBar={false}
        newestOnTop={false}
        closeOnClick
        rtl={false}
        pauseOnFocusLoss
        draggable
        pauseOnHover
        theme="light"
      />
    </div>
  );
};

export default ConfirmTicketPackage;
